/**
 * Licensed Materials - Property of IBM
 *
 * 'Restricted Materials of IBM'
 *
 * (C) Copyright IBM Corp. 2018
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import StringResources from '../../../nls/StringResources';
import { getGroupTypeAndGroupLabel } from './RelationshipUtil';

const SUPPORTED_GROUP_TYPES = ['none', 'hierarchy', 'concept'];

const GROUPING_TYPES = ['focusField', 'field',  'hierarchy', 'concept' ];
const FIELDS_ONLY_GROUPING_TYPES = ['focusField', 'field'];
const FOCUSFIELD = 'focusField';
const FIELD = 'field';
const GROUP_TYPE_NONE = 'none';
const GROUP_TYPE_NONE_PREFIX = 'none_';
const ITEMCLASS_SET_INDEX = { PARENT_ONLY: 0, PARENT_CHILD: 1, CHILD_ONLY: 2 };
/**
 * This class converts the smart module response from smart explore service into the Query Result data structure
 * that can be used to populate VIPRDATA
 */
export default class NodeDataset {

	constructor(rawData, metadataInfo, options){
		this.dataset = rawData? rawData.nodesDataset : { groupings:[] };
		this.metadataInfo = metadataInfo;
		this.options = options;
		this.groupingTypes = options.showConcepts ? GROUPING_TYPES : FIELDS_ONLY_GROUPING_TYPES;
		this.context = { 'targetId': this.dataset.targetId };
		this.reset();
	}

	reset(){
		this.nodeItemsList = [];/** Array that holds the frist mapped slot's data items*/
		this.colorItemsList = [];/** Array that holds the second mapped slot's data items*/
		this.dataPoints = [];/** Array that holds datapoints*/
		this.hierarchyInfos = {}; // used to display tooltip for hierarchy node
	}

	/**
	* @return {Object} return the context information of the node dataset
	*/
	getContext() {
		return this.context;
	}

	/**
	*@return {Object} return the options information of the node dataset
	*/
	getOptions(){
		return this.options;
	}

	_createItemClass_nodes() {
		return { 'itemClass':[{
			'h': [{
				'u': 'groupId',
				'd': 'groupLabel'
			}]
		},
		{
			'h': [{
				'u': 'groupId',
				'd': 'groupLabel'
			}, {
				'u': 'memberId',
				'd': 'memberLabel'
			}]
		},
		{
			'h': [{
				'u': 'memberId',
				'd': 'memberLabel'
			}]
		}
		] };
	}

	_createItemClass_confidence() {
		return { 'itemClass': {
			'h': [ {
				'u': 'Confidence',
				'd': 'Confidence',
				'aggregate': 'sum'
			} ]
		} };
	}

	_createItemClass_groupingType(){
		return { 'itemClass': {
			'h': [ {
				'u': 'groupingTypeId',
				'd': 'Legend'
			} ]
		} };
	}

	/**
	* Create data items list and datapoints from group node inside smart explore service response
	*
	* @param {JSONObject} groupNode
	* @param {String} groupType
	**/
	createItemsFromGroups(groupNode){
		if(groupNode && groupNode.members && groupNode.members.length >0){
			let oFieldItem;
			let nNodeIndex;

			const hierarchyLevels = [];
			const isHierarchy = (groupNode.groupType === 'hierarchy');
			if (isHierarchy) {
				this.hierarchyInfos[groupNode.groupId] = hierarchyLevels;
			}
			//Create data item for each field under the group (child)
			groupNode.members.forEach((memberEntry /*,memberIndex*/)=>{
				oFieldItem = groupNode.groupType === GROUP_TYPE_NONE ? this._createFieldOnlyItem(memberEntry.id)
					: this._createFieldItem(groupNode.groupId, groupNode.groupType, groupNode.groupLabel, memberEntry.id);
				nNodeIndex = this.nodeItemsList.push(oFieldItem) - 1 ;
				/*For Endor Release, the node confidence value will not be used, memberEntry.confidence
				*All node will have the same value 1
				*/
				this.addDatapoint(nNodeIndex, 1,  this._isFocusedField(memberEntry) ? FOCUSFIELD : FIELD);
				if (isHierarchy) {
					hierarchyLevels.push({
						levelNumber: memberEntry.level,
						levelLabel: this._getMetadataLabel(memberEntry.id)
					});
				}

			});
			if(groupNode.groupType !== GROUP_TYPE_NONE){
				const oGroupItem = this._createGroupItem(groupNode.groupId, groupNode.groupType, groupNode.groupLabel);
				nNodeIndex = this.nodeItemsList.push(oGroupItem) -1 ;
				//Group Node has NULl size
				this.addDatapoint(nNodeIndex, null, groupNode.groupType);
				if (isHierarchy) {
					hierarchyLevels.sort((lvl1, lvl2) => lvl1.levelNumber - lvl2.levelNumber);
				}
			}
		}
	}

	getHierarchyLevels(hierarchyNodeId) {
		const levels = this.hierarchyInfos[hierarchyNodeId];
		if (levels && levels.length > 1) {
			return levels[0].levelLabel + ' - ' +  levels[levels.length - 1].levelLabel;
		}
		return '';
	}

	_isFocusedField(memberEntry){
		return memberEntry.type === 'focusField';
	}

	/**
	*Create group data item using the passed in group data item
	*
	*@private
	*@param {String} groupUid	group data item unique ID
	*@param {String} groupType	group data item group type
	*@param {String} groupLabel	group data item group label
	**/
	_createGroupItem(groupUid, groupType, groupLabel){
		return {
			't': [{
				'u': groupUid,
				'd': this._getGroupLabel(groupUid, groupType, groupLabel)
			}],
			itemClassSetIndex: ITEMCLASS_SET_INDEX.PARENT_ONLY //parent node itself
		};
	}

	_createGroupingTypeItem(groupingType){
		return {
			't': [{
				'u': groupingType,
				'd': StringResources.get(groupingType)
			}]
		};
	}

	_getGroupLabel(groupUid, groupType, groupLabel){
		if(groupType === 'hierarchy' || groupType === 'concept'){
			return groupLabel;
		}
		return this._getMetadataLabel(groupLabel);
	}

	_createFieldItem(groupUid, groupType, groupLabel, fieldUid){
		return {
			't': [{
				'u': groupUid,
				'd': this._getGroupLabel(groupUid, groupType, groupLabel)
			},
			{
				'u': fieldUid,
				'd': this._getMetadataLabel(fieldUid)
			}],
			itemClassSetIndex: ITEMCLASS_SET_INDEX.PARENT_CHILD //Field node and its parent node
		};
	}

	_createFieldOnlyItem(fieldUid){
		return {
			't': [{
				'u': GROUP_TYPE_NONE_PREFIX + fieldUid,
				'd': this._getMetadataLabel(fieldUid)
			}],
			itemClassSetIndex: ITEMCLASS_SET_INDEX.CHILD_ONLY //Field node without parent node
		};
	}

	_getMetadataLabel(uid){
		if(this.metadataInfo){
			const field = this.metadataInfo.find(field => field.id === uid);
			if (field) {
				return field.name;
			}
		}
		return uid;
	}

	addDatapoint(nodeIndex, sizeValue, colorType) {
		const nColorType = this._findColorTypeIndex(colorType);
		this.dataPoints.push({ pt : [ nodeIndex, { v : sizeValue }, nColorType] });
	}

	_findColorTypeIndex(colorType){
		let nIndex = -1;
		this.groupingTypes.some((grouping, groupingIndex)=>{
			if(colorType.toLowerCase().startsWith(grouping.toLowerCase())){
				nIndex = groupingIndex;
				return true;
			}
		});
		return nIndex === -1 ? this.groupingTypes.length -1 : nIndex;
	}

	_createGroupingTypeItemsList() {
		this.groupingTypes.forEach((colorEntry)=>{
			this.colorItemsList.push(this._createGroupingTypeItem(colorEntry));
		});
	}

	_createGroupingTypeItemsListSection(){
		return Object.assign(this._createItemClass_groupingType(), { items: this.colorItemsList });
	}

	_createNodeItemsListSection(){
		return Object.assign(this._createItemClass_nodes(), { items: this.nodeItemsList });
	}

	_createQueryResult(){
		const aDataItemsList = [ this._createNodeItemsListSection(), this._createItemClass_confidence() ,this._createGroupingTypeItemsListSection() ];
		return Object.assign({ dataItems: aDataItemsList }, { data : this.dataPoints });
	}

	_isSupported_groupType(groupType) {
		return !!(groupType && SUPPORTED_GROUP_TYPES.indexOf(groupType) > -1);
	}

	/**
	* @return the converted JSON Object that can be consumed directly to create VIPRDATA
	*/
	getNodeDatasetQueryResult() {
		this.dataset.groupings.forEach((groupingEntry/*,groupingIndex*/)=>{
			groupingEntry.groups.forEach((groupEntry/*,groupsIndex*/)=>{
				if (!groupEntry.groupType) {
					// Deduct groupType & groupLabel from groupId if Smarts doesn't provide these infos
					const res = getGroupTypeAndGroupLabel(groupEntry.groupId);
					groupEntry.groupType = res.groupType;
					groupEntry.groupLabel = res.groupLabel;
				}
				if(this._isSupported_groupType(groupEntry.groupType)){
					this.createItemsFromGroups(groupEntry);
				}
			});
		});
		this._createGroupingTypeItemsList();
		return this._createQueryResult();
	}
}



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/NodeDataset.js